import { Injectable, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { finalize, Observable } from 'rxjs';
import { ApiService } from '../../../core/api.service';
import { NotificationItem } from '../models/notification';
import { MassNotification } from '../models/mass-notification';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly notificationsSignal = signal<NotificationItem[]>([]);
  private readonly loadingSignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);

  readonly notifications = computed(() => this.notificationsSignal());
  readonly loading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());
  readonly unreadCount = computed(() => this.notificationsSignal().filter((item) => !item.isRead).length);

  constructor(
    private readonly http: HttpClient,
    private readonly apiService: ApiService
  ) {}

  refresh(): void {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    this.http
      .get<NotificationItem[]>(this.apiService.buildUrl('api/notification/GetNotificationByUser'))
      .pipe(finalize(() => this.loadingSignal.set(false)))
      .subscribe({
        next: (items) => {
          this.notificationsSignal.set(items ?? []);
        },
        error: () => {
          this.errorSignal.set('Unable to load notifications.');
        }
      });
  }

  markAsRead(notificationId: string): void {
    this.http
      .put(this.apiService.buildUrl(`api/notification/${notificationId}/MarkAsRead`), {})
      .subscribe({
        next: () => {
          this.notificationsSignal.update((items) =>
            items.filter((notification) => notification.notificationId !== notificationId)
          );
        },
        error: () => {
          this.errorSignal.set('Unable to mark notification as read.');
        }
      });
  }

  markAllAsRead(): void {
    this.http.put(this.apiService.buildUrl('api/notification/MarkAllAsRead'), {}).subscribe({
      next: () => {
        this.notificationsSignal.set([]);
      },
      error: () => {
        this.errorSignal.set('Unable to mark all notifications as read.');
      }
    });
  }

  getMassNotifications(): Observable<MassNotification[]> {
    return this.http.get<MassNotification[]>(this.apiService.buildUrl('api/notification/getMassNotifications'));
  }

  inactivateMassNotifications(massNotificationIds: string[]): Observable<boolean> {
    return this.http.put<boolean>(
      this.apiService.buildUrl('api/notification/InactivateMassNotifications'),
      massNotificationIds
    );
  }

  getMassNotificationCount(payload: MassNotification): Observable<number> {
    return this.http.put<number>(
      this.apiService.buildUrl('api/notification/getMassNotificationCount'),
      payload
    );
  }

  createMassNotification(payload: MassNotification): Observable<MassNotification> {
    return this.http.post<MassNotification>(
      this.apiService.buildUrl('api/notification/createMassNotification'),
      payload
    );
  }
}
